import React, { useState, useMemo } from 'react';
import { 
  Mail, 
  Bell, 
  MessageCircle, 
  Send, 
  Filter, 
  Search, 
  Plus, 
  Edit, 
  Trash2,
  AlertTriangle,
  Info,
  CheckCircle2,
  UserCheck
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Type Definitions
interface NotificationTemplate {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'push';
  subject?: string;
  content: string;
  category: string;
}

interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  status: 'draft' | 'scheduled' | 'sent' | 'completed';
  scheduledDate?: string;
  recipientCount: number;
  openRate?: number;
}

interface SystemAnnouncement {
  id: string;
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high';
  publishDate: string;
  expiryDate?: string;
}

interface SupportTicket {
  id: string;
  title: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high';
  assignedTo?: string;
  createdBy: string;
  createdAt: string;
  lastUpdated: string;
}

interface AutomatedAlert {
  id: string;
  type: 'system' | 'security' | 'performance' | 'compliance';
  message: string;
  severity: 'info' | 'warning' | 'critical';
  timestamp: string;
  read: boolean;
}

const CommunicationManagement: React.FC = () => {
  // Breadcrumb Management
  useBreadcrumbs([
        { label: 'Escrow Management', href: '#' },
        { label: 'Escrow Communication Management' }
      ]);
  // State Management
  const [notificationTemplates, setNotificationTemplates] = useState<NotificationTemplate[]>([
    {
      id: 'TEMPLATE001',
      name: 'Welcome Email',
      type: 'email',
      subject: 'Welcome to Our Platform',
      content: 'Dear {username}, Welcome to our platform! We are excited to have you on board.',
      category: 'Onboarding'
    }
  ]);

  const [emailCampaigns, setEmailCampaigns] = useState<EmailCampaign[]>([
    {
      id: 'CAMPAIGN001',
      name: 'Monthly Newsletter',
      subject: 'Latest Updates and Insights',
      status: 'scheduled',
      scheduledDate: '2024-03-01',
      recipientCount: 5000,
      openRate: 42.5
    }
  ]);

  const [systemAnnouncements, setSystemAnnouncements] = useState<SystemAnnouncement[]>([
    {
      id: 'ANNOUNCE001',
      title: 'Platform Maintenance',
      content: 'We will be performing system maintenance on March 15th from 2-4 AM UTC.',
      priority: 'medium',
      publishDate: '2024-02-25',
      expiryDate: '2024-03-15'
    }
  ]);

  const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([
    {
      id: 'TICKET001',
      title: 'Login Issues',
      status: 'open',
      priority: 'high',
      assignedTo: 'Support Team',
      createdBy: 'john.doe@example.com',
      createdAt: '2024-02-20',
      lastUpdated: '2024-02-22'
    }
  ]);

  const [automatedAlerts, setAutomatedAlerts] = useState<AutomatedAlert[]>([
    {
      id: 'ALERT001',
      type: 'security',
      message: 'Multiple failed login attempts detected',
      severity: 'warning',
      timestamp: '2024-02-25 10:30:45',
      read: false
    }
  ]);

  // Filtering States
  const [templateFilter, setTemplateFilter] = useState('all');
  const [templateSearch, setTemplateSearch] = useState('');

  // Render Notification Templates
  const renderNotificationTemplates = () => {
    // Filter Templates
    const filteredTemplates = notificationTemplates.filter(template => {
      const matchesType = templateFilter === 'all' || template.type === templateFilter;
      const matchesSearch = template.name.toLowerCase().includes(templateSearch.toLowerCase());
      return matchesType && matchesSearch;
    });

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Send className="mr-2 h-5 w-5 text-blue-500" /> 
            Notification Templates
          </h2>
          <div className="flex items-center space-x-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search templates"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                className="pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-2 top-3 h-4 w-4 text-gray-400" />
            </div>
            <select
              value={templateFilter}
              onChange={(e) => setTemplateFilter(e.target.value)}
              className="pl-8 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="push">Push</option>
            </select>
            <button 
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
              onClick={() => {
                // TODO: Implement add template logic
              }}
            >
              <Plus className="mr-2 h-4 w-4" /> New Template
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTemplates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {template.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 uppercase">
                    {template.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {template.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {template.subject || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button 
                      className="text-blue-600 hover:text-blue-900"
                      onClick={() => {
                        // TODO: Implement edit template logic
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button 
                      className="text-red-600 hover:text-red-900"
                      onClick={() => {
                        // TODO: Implement delete template logic
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Render Email Campaigns
  const renderEmailCampaigns = () => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Send className="mr-2 h-5 w-5 text-green-500" /> 
            Email Campaigns
          </h2>
          <button 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
            onClick={() => {
              // TODO: Implement create campaign logic
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> New Campaign
          </button>
        </div>
        <div className="space-y-4">
          {emailCampaigns.map((campaign) => (
            <div 
              key={campaign.id} 
              className="bg-gray-50 p-4 rounded-lg border border-gray-200"
            >
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-gray-900">{campaign.name}</h3>
                  <p className="text-sm text-gray-500">{campaign.subject}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    campaign.status === 'scheduled'
                      ? 'bg-yellow-100 text-yellow-800'
                      : campaign.status === 'sent'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                  </span>
                  <span className="text-sm text-gray-500">
                    Recipients: {campaign.recipientCount}
                  </span>
                </div>
              </div>
              {campaign.scheduledDate && (
                <div className="mt-2 text-sm text-gray-500">
                  Scheduled: {campaign.scheduledDate}
                </div>
              )}
              {campaign.openRate !== undefined && (
                <div className="mt-2 text-sm text-gray-500">
                  Open Rate: {campaign.openRate.toFixed(1)}%
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <MessageCircle className="mr-3 h-8 w-8 text-blue-600" />
            Communication Management
          </h1>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Notification Templates */}
            <div>
              {renderNotificationTemplates()}
            </div>

            {/* Email Campaigns */}
            <div>
              {renderEmailCampaigns()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunicationManagement;